import { Button, Input, Table, Pagination } from 'app/components';
import styles from './PortDetails.module.scss';

import { useGetPortTargetCatalogMutation, useGetVesselsInPortCsvMutation } from 'app/api/facade';
import { shipCallsColumns } from './PortDetailsTableColumns';
import { t } from 'i18next';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { throttle } from 'app/helpers';
import { useAppSelector } from 'app/store/store';
import { useParams } from 'react-router-dom';
import { Icon } from 'app/components/Icon';

import { Typo } from 'app/components/Typo';
import { PortTargetCatalogFilter, PortTargetCatalogOrderParams, PortTargetCatalogRequest } from 'ais-facade-api/api';
import { useDebounce, useOnClickOutside } from 'app/hooks';
import moment from 'moment';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { PortTargetCatalogFilterPanel } from 'app/components/FilterPanel/PortTargetCatalogFilterPanel';
import { handleFileDownload } from "../../helpers/handleFileDownload";
import { getI18n } from 'react-i18next';

export interface PortTargetCatalogFilters extends PortTargetCatalogFilter {
  touched?: boolean;
  typeAll: boolean;
  countryAll: boolean;
  fleetGroupAll: boolean;
  term?: string;
}
export interface PortTargetCatalogSortingInterface {
  direction: PortTargetCatalogOrderParams['direction'] | 'default';
  by: PortTargetCatalogOrderParams['by'];
}

const initialSort: PortTargetCatalogOrderParams = {
  direction: 'ASC',
  by: 'MMSI',
};

export const PortVessels = () => {
  const language = getI18n().language;
  const { portKey } = useParams();
  const { addAlert } = useAlertHook();

  const user = useAppSelector((state) => state.userSlice.user);
  const [getCSV] = useGetVesselsInPortCsvMutation();

  const curDate = useMemo(() => moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'), []);

  const [getTargetCatalog, { data: targetCatalog, ...targetCatalogRequest }] = useGetPortTargetCatalogMutation();
  const debounceGetTargetssHandler = useCallback(throttle(getTargetCatalog, 500), []);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  useEffect(() => {
    targetCatalogRequest.reset();
  }, []);

  const [sorting, setSorting] = useState<PortTargetCatalogSortingInterface>(initialSort);
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebounce<string>(inputValue, 500);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState<PortTargetCatalogFilters>();
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(() => shipCallsColumns, []);


  const initialBody: PortTargetCatalogRequest = useMemo(
    () => ({
      filter: {
        fleetOnly: false,
        fleetGroup: [],
        type: [],
        country: [],
      },
      order: initialSort,
      pagination: {
        offset: 0,
        limit: 20,
      },
    }),
    [curDate, initialSort],
  );
  const handleExport = () => {
    portKey &&
      getCSV({
        key: portKey,
        body: {
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            fleetOnly: filters?.fleetOnly || false,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : undefined,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      })
        .unwrap()
        .then(handleFileDownload)
        .catch((error) => addAlert(error));
  };

  useEffect(() => {
    setCurrentPage(1);
    user &&
      debounceGetTargetssHandler({
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
          pagination: {
            offset: currentOffset,
            limit: 20,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
        requestParam: portKey,
      });
  }, [debouncedInputValue]);

  useEffect(() => {
    if (user) {
      debounceGetTargetssHandler({
        requestParam: portKey,
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
          pagination: {
            offset: currentOffset,
            limit: 20,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [currentOffset, user, language]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      debounceGetTargetssHandler({
        requestParam: portKey,
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },

          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setSorting(initialBody.order);

      debounceGetTargetssHandler({
        requestParam: portKey,
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            // eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
        },
      });
    }
  }, [filters]);

  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  return (
    <>
      <div className={styles.table_header}>
        <Typo
          value={`${t('PortTargetCatalog')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${typeof targetCatalog?.total === 'number' ? targetCatalog.total : '–'}`}
          size="m"
        />
        <div className={styles.table_actions}>
          <Button variant="general" onClick={() => setShowFilter(true)} className={styles.icon_button}>
            {filters?.touched && <div className={styles.indicator} />}
            <Icon name="filter" />
            <Typo value={t('Filter')} variant="p" />
          </Button>
          <Button
            label={t('ButtonLabelExportSCV')}
            variant="general"
            className={styles.icon_button}
            onClick={handleExport}
            disabled={!handleExport}
          />
          <Input
            className={styles.table_search}
            type="search"
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('SearchPlaceholder')}
            value={inputValue}
          />
        </div>
      </div>

      {targetCatalogRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!targetCatalogRequest.isLoading && targetCatalog?.results?.length === 0 && (
        <p className={styles.no_data}>{t('NoResultsFound')}</p>
      )}
      {targetCatalog?.results && targetCatalog.total && targetCatalog.results.length > 0 ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={targetCatalog.results} setSorting={setSorting} />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={targetCatalog.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      ) : null}

      {showFilter && (
        <PortTargetCatalogFilterPanel
          filters={filters}
          handleFilters={setFilters}
          innerRef={filterPanelRef}
          onClose={() => {
            setShowFilter(false);
          }}
        />
      )}
    </>
  );
};
